import {useId, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {tr, trx, type Texto} from '../i18n/idioma';

// Nota de ayuda de un campo (Daniel, 14/10/2026): al pasar el cursor (o al
// tocar ⓘ) dice qué es el campo y cómo se llena. Se dibuja sobre la página,
// fuera de la tabla, para que no la corte el borde ni el encabezado fijo.
// Dentro de un <label> (dentroDeEtiqueta) la i no entra en el nombre del campo:
// asi el campo se sigue llamando igual para el lector de pantalla y las pruebas.
export function NotaDeAyuda({
  texto,
  titulo,
  dentroDeEtiqueta = false,
}: {
  texto: Texto | string;
  titulo?: Texto | string;
  dentroDeEtiqueta?: boolean;
}) {
  const [abierta, setAbierta] = useState(false);
  const [posicion, setPosicion] = useState<{top: number; left: number} | null>(null);
  const ancla = useRef<HTMLSpanElement>(null);
  const nota = useRef<HTMLDivElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!abierta || !ancla.current) return;
    const r = ancla.current.getBoundingClientRect();
    const ancho = nota.current?.offsetWidth ?? 260;
    const alto = nota.current?.offsetHeight ?? 80;
    const left = Math.max(8, Math.min(r.left + r.width / 2 - ancho / 2, window.innerWidth - ancho - 8));
    // Debajo del ⓘ; si no cabe, encima.
    const top = r.bottom + 6 + alto > window.innerHeight ? Math.max(8, r.top - alto - 6) : r.bottom + 6;
    setPosicion({top, left});
  }, [abierta]);

  return (
    <>
      {/* Un span con rol de boton (no un <button>): asi la i funciona tambien en un
          formulario bloqueado (fieldset deshabilitado) y dentro de una tarjeta-boton. */}
      <span
        ref={ancla}
        role="button"
        aria-label={dentroDeEtiqueta ? undefined : tr('Ayuda de este campo', 'Help for this field')}
        aria-hidden={dentroDeEtiqueta || undefined}
        tabIndex={dentroDeEtiqueta ? -1 : 0}
        aria-describedby={abierta ? id : undefined}
        onMouseEnter={() => setAbierta(true)}
        onMouseLeave={() => setAbierta(false)}
        onFocus={() => setAbierta(true)}
        onBlur={() => setAbierta(false)}
        onClick={e => {
          // Dentro de un encabezado no debe ordenar ni arrastrar.
          e.stopPropagation();
          e.preventDefault();
          setAbierta(x => !x);
        }}
        onKeyDown={e => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          setAbierta(x => !x);
        }}
        onMouseDown={e => e.stopPropagation()}
        draggable={false}
        // La i se dibuja con estilo (no es texto): asi no entra en el nombre del campo.
        // Va en linea con el texto (no como caja aparte): si el nombre no cabe,
        // baja la ultima palabra con la i y la i nunca queda sola debajo (Daniel, 20/10/2026).
        className="ml-1 inline cursor-help rounded-full border border-linea px-[5px] py-px align-[1px] text-[10px] leading-none font-normal whitespace-nowrap normal-case text-apagado before:content-['i'] hover:border-verde hover:text-verde-oscuro"
      />
      {abierta &&
        createPortal(
          <div
            ref={nota}
            id={id}
            role="tooltip"
            style={{top: posicion?.top ?? -9999, left: posicion?.left ?? -9999}}
            className="pointer-events-none fixed z-50 max-w-72 rounded-md border border-linea bg-white px-3 py-2 text-left text-xs leading-snug font-normal tracking-normal normal-case whitespace-normal text-tinta shadow-lg"
          >
            {titulo && <p className="mb-0.5 font-semibold text-verde-oscuro">{trx(titulo)}</p>}
            <p>{trx(texto)}</p>
          </div>,
          document.body,
        )}
    </>
  );
}
